import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { createHttpContext } from '@app/core/http/http-context';
import { ITenantService } from '@app/shared/services/itenant.service';
import { Response } from '@app/shared/services/local/Response';
import { environment } from '@env/environment';
import { firstValueFrom, Observable } from 'rxjs';

import { activationApiAvailable } from '../activation-link.service';
import { ACTIVATION_CHECKOUT_PREFETCH_TTL_MS } from '../tenant-lifecycle.config';
import {
  ActivationCheckout,
  ActivationConfirmResult,
  activationConflictReason,
  ActivationConflictReason,
  parseActivationCheckout,
  parseActivationConfirmResult,
  parseSetupIntentSecret,
} from './activation-checkout';

/**
 * `forbidden`: not a Company Administrator (403). `conflict`: a 409 with a known
 * reason. `unavailable`: everything else (502 activation_unavailable, network,
 * an answer outside the contract).
 */
export type ActivationCheckoutFailure = 'forbidden' | 'conflict' | 'unavailable';

export class ActivationCheckoutError extends Error {
  constructor(
    readonly failure: ActivationCheckoutFailure,
    readonly reason: ActivationConflictReason | null = null,
  ) {
    super(`activation_checkout_${reason ?? failure}`);
    this.name = 'ActivationCheckoutError';
  }
}

/**
 * HTTP side of the in-app activation (see activation-checkout.ts for the
 * contract). GET api/v1/tenant/activation can be prefetched so a click knows
 * right away whether it opens the modal, the contact link or the old link flow.
 */
@Service()
export class ActivationCheckoutService {
  private readonly http = inject(HttpClient);
  private readonly tenantService = inject(ITenantService, { optional: true });

  private cached: { checkout: ActivationCheckout; at: number } | null = null;
  private pending: Promise<ActivationCheckout> | null = null;

  /** Test seam for the clock that decides whether a prefetched answer is still fresh. */
  now: () => number = () => Date.now();

  /** A fresh answer from an earlier load or prefetch, without asking again. */
  peek(): ActivationCheckout | null {
    const cached = this.cached;
    return cached !== null && this.now() - cached.at < ACTIVATION_CHECKOUT_PREFETCH_TTL_MS ? cached.checkout : null;
  }

  /** Forgets the cached answer, e.g. after the plan was booked. */
  invalidate(): void {
    this.cached = null;
  }

  /** Loads in the background; failures stay silent, the click asks again. */
  prefetch(): Promise<ActivationCheckout | null> {
    const fresh = this.peek();
    if (fresh !== null) {
      return Promise.resolve(fresh);
    }
    return this.load().catch(() => null);
  }

  /** GET api/v1/tenant/activation. Concurrent callers share one request. */
  load(): Promise<ActivationCheckout> {
    this.pending ??= this.request().finally(() => {
      this.pending = null;
    });
    return this.pending;
  }

  /** POST api/v1/tenant/activation/setup-intent: client secret for confirmSetup. */
  async createSetupIntent(): Promise<string> {
    const data = await this.send(this.http.post<Response<unknown>>('tenant/activation/setup-intent', {}, options()));
    const clientSecret = parseSetupIntentSecret(data);
    if (clientSecret == null) {
      throw new ActivationCheckoutError('unavailable');
    }
    return clientSecret;
  }

  /** POST api/v1/tenant/activation/confirm. `setupIntentId` null keeps the card on file. */
  async confirm(priceId: string, setupIntentId: string | null): Promise<ActivationConfirmResult> {
    const data = await this.send(
      this.http.post<Response<unknown>>('tenant/activation/confirm', { priceId, setupIntentId }, options()),
    );
    const result = parseActivationConfirmResult(data);
    if (result === null) {
      throw new ActivationCheckoutError('unavailable');
    }
    return result;
  }

  /**
   * POST api/v1/tenant/activation/confirm-saved: books the price with the
   * payment method saved on the customer. `paymentMethodId` null lets the
   * master take the one it offered (an older master sends no id).
   */
  async confirmSaved(paymentMethodId: string | null, priceId: string): Promise<ActivationConfirmResult> {
    const data = await this.send(
      this.http.post<Response<unknown>>('tenant/activation/confirm-saved', { paymentMethodId, priceId }, options()),
    );
    const result = parseActivationConfirmResult(data);
    if (result === null) {
      throw new ActivationCheckoutError('unavailable');
    }
    return result;
  }

  private async request(): Promise<ActivationCheckout> {
    const tenant = this.tenantService?.tenantSignal() ?? null;
    if (!activationApiAvailable(environment.environmentName, environment.serverUrl, tenant?.apiUrl)) {
      throw new ActivationCheckoutError('unavailable');
    }
    const checkout = parseActivationCheckout(
      await this.send(this.http.get<Response<unknown>>('tenant/activation', options())),
    );
    if (checkout === null) {
      throw new ActivationCheckoutError('unavailable');
    }
    this.cached = { checkout, at: this.now() };
    return checkout;
  }

  private async send(request: Observable<Response<unknown>>): Promise<unknown> {
    try {
      const response = await firstValueFrom(request);
      return response?.data;
    } catch (error: unknown) {
      throw toCheckoutError(error);
    }
  }
}

function options(): { context: ReturnType<typeof createHttpContext> } {
  // The modal shows its own messages; the global error toast would only duplicate them.
  return { context: createHttpContext({ skipErrorHandler: true }) };
}

function toCheckoutError(error: unknown): ActivationCheckoutError {
  const reason = activationConflictReason(error);
  if (reason !== null) {
    return new ActivationCheckoutError('conflict', reason);
  }
  return new ActivationCheckoutError(
    error instanceof HttpErrorResponse && error.status === 403 ? 'forbidden' : 'unavailable',
  );
}
